import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import Avatar from '../../components/Avatar';
import { Plus, User, Phone, Mail, Edit2, Trash2, AlertTriangle, Send, Check, X } from 'lucide-react';

function ConfirmDeleteModal({ name, type, onConfirm, onClose }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-900">Permanently Delete {type}</h2>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">
            You are about to permanently delete <strong>{name}</strong> and all associated data including:
          </p>
          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>All gig assignments</li>
            <li>Payment history</li>
            <li>Notes and records</li>
          </ul>
          <div>
            <label className="label">Type DELETE to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="input font-mono"
              placeholder="DELETE"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleConfirm}
              disabled={confirmText !== 'DELETE' || deleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </button>
            <button onClick={onClose} className="btn-secondary flex-1" disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteModal({ contractor, onClose }) {
  const [method, setMethod] = useState('both');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const hasPhone = !!contractor.phone;
  const hasEmail = !!contractor.email;

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const response = await api.post('/auth/invite', {
        userId: contractor.id,
        method
      });
      setResult({ success: true, ...response });
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Invite {contractor.name}</h2>
          <p className="text-sm text-gray-500">Send login credentials to access Hubtractor</p>
        </div>

        <div className="p-4 space-y-4">
          {result ? (
            // Show result
            <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-3">
                {result.success ? (
                  <Check className="w-6 h-6 text-green-600" />
                ) : (
                  <X className="w-6 h-6 text-red-600" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${result.success ? 'text-green-900' : 'text-red-900'}`}>
                    {result.success ? 'Invite Sent!' : 'Failed to Send'}
                  </p>
                  {result.success && result.sent && (
                    <div className="text-sm text-green-700 mt-1">
                      {result.sent.map((s, i) => (
                        <p key={i}>✓ Sent via {s.method} to {s.to}</p>
                      ))}
                    </div>
                  )}
                  {result.error && (
                    <p className="text-sm text-red-700 mt-1">{result.error}</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Show form
            <>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Send login credentials via:</p>
                
                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'both' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${(!hasPhone || !hasEmail) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="both"
                    checked={method === 'both'}
                    onChange={() => setMethod('both')}
                    disabled={!hasPhone || !hasEmail}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Both SMS & Email</p>
                    <p className="text-xs text-gray-500">Send to phone and email</p>
                  </div>
                </label>

                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'sms' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${!hasPhone ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="sms"
                    checked={method === 'sms'}
                    onChange={() => setMethod('sms')}
                    disabled={!hasPhone}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">SMS Only</p>
                    <p className="text-xs text-gray-500">{contractor.phone || 'No phone number'}</p>
                  </div>
                  <Phone className="w-4 h-4 text-gray-400" />
                </label>

                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'email' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${!hasEmail ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="email"
                    checked={method === 'email'}
                    onChange={() => setMethod('email')}
                    disabled={!hasEmail}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Email Only</p>
                    <p className="text-xs text-gray-500">{contractor.email || 'No email'}</p>
                  </div>
                  <Mail className="w-4 h-4 text-gray-400" />
                </label>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            {result ? (
              <button onClick={onClose} className="btn-primary flex-1">
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleSend}
                  disabled={sending || (!hasPhone && !hasEmail)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Invite
                    </>
                  )}
                </button>
                <button onClick={onClose} className="btn-secondary flex-1" disabled={sending}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContractorModal({ contractor, onSave, onClose }) {
  const [name, setName] = useState(contractor?.name || '');
  const [displayName, setDisplayName] = useState(contractor?.displayName || '');
  const [email, setEmail] = useState(contractor?.email || '');
  const [phone, setPhone] = useState(contractor?.phone || '');
  const [active, setActive] = useState(contractor?.active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (contractor?.id) {
        await api.put(`/contractors/${contractor.id}`, { name, displayName, email, phone, active });
      } else {
        await api.post('/contractors', { name, displayName, email, phone });
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save contractor');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">{contractor?.id ? 'Edit Contractor' : 'New Contractor'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Display Name</label>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)} className="input" placeholder="Stage name for invoices (optional)" />
          </div>
          <div>
            <label className="label">Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Phone *</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} className="input" required placeholder="+1..." />
          </div>
          {contractor?.id && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="rounded" />
              <span className="text-sm">Active</span>
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminContractors() {
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [inviteModal, setInviteModal] = useState(null);

  const isOwner = user?.role === 'OWNER';

  const loadContractors = () => {
    api.get('/contractors').then(setContractors).finally(() => setLoading(false));
  };

  useEffect(() => { loadContractors(); }, []);

  const deactivateContractor = async (id, name) => {
    if (!confirm(`Deactivate ${name}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/contractors/${id}`);
      loadContractors();
    } catch (err) {
      alert(err.message);
    }
  };

  const permanentlyDeleteContractor = async (id) => {
    try {
      await api.delete(`/contractors/${id}/permanent`, { confirm: 'DELETE' });
      setDeleteModal(null);
      loadContractors();
    } catch (err) {
      alert(err.message);
      setDeleteModal(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contractors</h1>
        <button onClick={() => setModal({})} className="btn-primary"><Plus className="w-4 h-4" />Add Contractor</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contractors.map(c => (
          <div key={c.id} className="card">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar name={c.name} photo={c.profilePhoto} size="md" />
                  <div>
                    <h3 className="font-medium">{c.name}</h3>
                    <p className="text-sm text-gray-500">{c._count?.assignments || 0} gigs completed</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setModal(c)} className="btn-ghost p-2" title="Edit"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => deactivateContractor(c.id, c.name)} className="btn-ghost p-2 text-red-600 hover:bg-red-50" title="Deactivate"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{c.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Phone className="w-4 h-4" />
                  <span>{c.phone}</span>
                </div>
              </div>
              
              {!c.active && <span className="badge-cancelled mt-2">Inactive</span>}

              {/* Invite Button */}
              {c.active && (
                <button
                  onClick={() => setInviteModal(c)}
                  className="mt-4 w-full btn-secondary flex items-center justify-center gap-2 text-sm"
                >
                  <Send className="w-4 h-4" />
                  Send Login Invite
                </button>
              )}
              
              {/* Permanent Delete - Owner Only */}
              {isOwner && (
                <div className="mt-3 pt-3 border-t">
                  <button
                    onClick={() => setDeleteModal(c)}
                    className="text-xs text-red-600 hover:text-red-800 hover:underline"
                  >
                    Permanently delete...
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {modal && <ContractorModal contractor={modal.id ? modal : null} onSave={() => { setModal(null); loadContractors(); }} onClose={() => setModal(null)} />}
      
      {deleteModal && (
        <ConfirmDeleteModal
          name={deleteModal.name}
          type="Contractor"
          onConfirm={() => permanentlyDeleteContractor(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}

      {inviteModal && (
        <InviteModal
          contractor={inviteModal}
          onClose={() => setInviteModal(null)}
        />
      )}
    </div>
  );
} 
